import {
    AiConfidenceScoreModel,
    AiFactorBreakdown,
    AiMetadataProperties
} from "../../../store/Models/Surveys/SurveyAiMetadata";
import { AiAnswerSourceItem, ConfidenceFactorRow, HelpfulSelection } from "./aiAnswerMetadata.types";

const INSUFFICIENT_CONFIDENCE_LABEL = "Insufficient Data";
const HIGH_CONFIDENCE_LABEL = "High Confidence";
const LOW_CONFIDENCE_LABEL = "Low Confidence";
const DEFAULT_CONFIDENCE_LABEL = INSUFFICIENT_CONFIDENCE_LABEL;

const normalizeConfidenceLabel = (label: string | null | undefined): string => (
    (label ?? "").trim().toLowerCase()
);

const CONFIDENCE_FACTOR_KEYS: Array<{ label: string; key: keyof AiFactorBreakdown }> = [
    { label: "Source Strength", key: "sourceStrength" },
    { label: "Document Match Quality", key: "matchQuality" },
    { label: "Extraction Precision", key: "citationQuality" },
    { label: "Date Recency", key: "batchRecency" },
    { label: "Regulatory Judgement", key: "regulatoryConfidence" }
];

const parseNumericValue = (value: unknown): number | null => {
    if (typeof value === "number" && Number.isFinite(value)) {
        return value;
    }

    if (typeof value === "string") {
        const parsed = Number.parseFloat(value);

        if (Number.isFinite(parsed)) {
            return parsed;
        }
    }

    return null;
};

const getFactorValue = (
    factorBreakdown: Record<string, unknown> | null | undefined,
    factorKey: keyof AiFactorBreakdown
): number | null => {
    if (!factorBreakdown) {
        return null;
    }

    return parseNumericValue(factorBreakdown[factorKey]);
};

export const normalizeHelpfulSelection = (isHelpful: number | null | undefined): HelpfulSelection => (
    isHelpful === 1 ? 1 :
    isHelpful === -1 ? -1 :
    0
);

export const toggleHelpfulSelection = (
    currentSelection: HelpfulSelection,
    selectedValue: -1 | 1
): HelpfulSelection => (
    currentSelection === selectedValue ? 0 : selectedValue
);

export const mapAiSources = (aiMetadata: AiMetadataProperties | null | undefined): AiAnswerSourceItem[] => (
    aiMetadata?.citations ?? []
).map(citation => ({
    document: citation.document || "",
    pageNumber: citation.pageNumber?.toString().trim() || undefined,
    content: citation.contentPreview || undefined
}));

export const getConfidenceLabel = (confidenceScore: AiConfidenceScoreModel | null | undefined): string => {
    const confidenceLevel = confidenceScore?.confidenceLevel;
    const normalizedConfidenceLevel = normalizeConfidenceLabel(confidenceLevel);

    if (normalizedConfidenceLevel.length === 0) {
        return DEFAULT_CONFIDENCE_LABEL;
    }

    if (normalizedConfidenceLevel === normalizeConfidenceLabel(HIGH_CONFIDENCE_LABEL)) {
        return HIGH_CONFIDENCE_LABEL;
    }

    if (normalizedConfidenceLevel === normalizeConfidenceLabel(LOW_CONFIDENCE_LABEL)) {
        return LOW_CONFIDENCE_LABEL;
    }

    if (normalizedConfidenceLevel === normalizeConfidenceLabel(INSUFFICIENT_CONFIDENCE_LABEL)) {
        return DEFAULT_CONFIDENCE_LABEL;
    }

    return confidenceLevel?.trim() ?? DEFAULT_CONFIDENCE_LABEL;
};

export const isInsufficientDataConfidenceLabel = (confidenceLabel: string | null | undefined): boolean => (
    normalizeConfidenceLabel(confidenceLabel) === normalizeConfidenceLabel(INSUFFICIENT_CONFIDENCE_LABEL)
);

export const normalizeJsonStringForComparison = (value: string | null | undefined): string => {
    const trimmedValue = value?.trim() ?? "";

    if (trimmedValue.length === 0) {
        return "";
    }

    try {
        return JSON.stringify(JSON.parse(trimmedValue));
    } catch {
        return trimmedValue;
    }
};

export const getConfidenceFactorRows = (
    confidenceScore: AiConfidenceScoreModel | null | undefined
): ConfidenceFactorRow[] => {
    if (!confidenceScore) {
        return [];
    }

    const factorBreakdown = confidenceScore.factorBreakdown as Record<string, unknown> | null | undefined;

    return CONFIDENCE_FACTOR_KEYS
        .map(row => ({ label: row.label, value: getFactorValue(factorBreakdown, row.key) }))
        .filter(row => row.value !== null)
        .map(row => ({ label: row.label, value: row.value as number }));
};

export const getConfidenceReasoning = (
    confidenceScore: AiConfidenceScoreModel | null | undefined
): string => confidenceScore?.confidenceReasoning || "";

export const hasAiMetadataContent = (
    reasoning: string | null | undefined,
    sources: AiAnswerSourceItem[],
    confidenceScore: AiConfidenceScoreModel | null | undefined
): boolean => {
    const hasReasoning = (reasoning ?? "").trim().length > 0;
    const hasSources = sources.length > 0;
    const hasConfidenceDetails = !!confidenceScore;

    return hasReasoning || hasSources || hasConfidenceDetails;
};

export const isAiAnswerModified = (
    currentValue: string | null | undefined,
    aiSuggestedValue: string | null | undefined,
    hasAiMetadata: boolean
): boolean => {
    if (!hasAiMetadata) {
        return false;
    }

    const normalizedCurrentValue = currentValue ?? "";
    const normalizedAiSuggestedValue = aiSuggestedValue ?? "";

    if (normalizedAiSuggestedValue.trim().length === 0) {
        return false;
    }

    return normalizedCurrentValue !== normalizedAiSuggestedValue;
};
